import React, { useRef, useState, useEffect } from "react";
import "./Whiteboard.css";

export default function Whiteboard() {
  const canvasRef = useRef(null);

  const [isDrawing, setIsDrawing] = useState(false);
  const [color, setColor] = useState("#000000");
  const [lineWidth, setLineWidth] = useState(2);
  const [mode, setMode] = useState("draw");

  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [textValue, setTextValue] = useState("Text Here");

  // Shape drawing
  const shapeStart = useRef(null);
  const previewCanvasRef = useRef(null);

  
  // Pan
  const [isPanning, setIsPanning] = useState(false);
  const startPan = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });
  const lastPosRef = useRef({ x: 0, y: 0 });
  const boardRef = useRef(null);

  // High resolution canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const dpr = window.devicePixelRatio || 1;
    const width = 4000;
    const height = 2600;

    canvas.width = width * dpr;
    canvas.height = height * dpr;

    canvas.style.width = width + "px";
    canvas.style.height = height + "px";

    canvas.style.background = "#ffffff";
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    // Setup preview canvas
    const previewCanvas = previewCanvasRef.current;
    if (previewCanvas) {
      const previewCtx = previewCanvas.getContext("2d");
      previewCanvas.width = canvas.width;
      previewCanvas.height = canvas.height;
      previewCanvas.style.width = canvas.style.width;
      previewCanvas.style.height = canvas.style.height;
      previewCtx.scale(dpr, dpr);
      previewCtx.lineCap = "round";
      previewCtx.lineJoin = "round";
    }

    saveState();
  }, []);

  const getCtx = () => canvasRef.current.getContext("2d");

  const getCoordinates = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const startDrawing = (e) => {
    if (mode === "pan") return;

    const { x, y } = getCoordinates(e);
    const ctx = getCtx();
    ctx.beginPath();
    ctx.globalCompositeOperation = mode === "erase" ? "destination-out" : "source-over";
    ctx.strokeStyle = color;
    ctx.lineWidth = mode === "erase" ? Math.max(18, lineWidth * 5) : Math.max(4, lineWidth * 2);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.setLineDash([]);
    ctx.moveTo(x, y);
    lastPosRef.current = { x, y };
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;

    const { x, y } = getCoordinates(e);
    const ctx = getCtx();

    ctx.lineTo(x, y);
    ctx.stroke();
    lastPosRef.current = { x, y };
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      saveState();
    }
  };

  const saveState = () => {
    const data = canvasRef.current.toDataURL();
    setHistory((prev) => [...prev, data]);
    setRedoStack([]);
  };

  const undo = () => {
    if (history.length <= 1) return;

    const newHistory = [...history];
    const last = newHistory.pop();

    setRedoStack((prev) => [...prev, last]);
    setHistory(newHistory);

    restore(newHistory[newHistory.length - 1]);
  };

  const redo = () => {
    if (redoStack.length === 0) return;

    const last = redoStack[redoStack.length - 1];
    setRedoStack((prev) => prev.slice(0, -1));
    setHistory((prev) => [...prev, last]);

    restore(last);
  };

  const restore = (dataUrl) => {
    const img = new Image();
    img.src = dataUrl;
    img.onload = () => {
      const ctx = getCtx();
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      ctx.drawImage(img, 0, 0);
    };
  };

  const clearBoard = () => {
    const ctx = getCtx();
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    saveState();
  };

  const saveBoard = async () => {
    const image = canvasRef.current.toDataURL("image/png");

    await fetch("http://localhost:5000/save-board", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ image }),
    });

    alert("Saved!");
  };

  const placeText = (e) => {
    const { x, y } = getCoordinates(e);
    const ctx = getCtx();
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = color;
    ctx.font = `${Math.max(18, lineWidth * 7)}px sans-serif`;
    ctx.fillText(textValue || "Text", x, y);
    saveState();
  };

  const getPreviewCtx = () => previewCanvasRef.current?.getContext("2d");

  const drawShapePreview = (start, end) => {
    const previewCtx = getPreviewCtx();
    if (!previewCtx) return;
    previewCtx.clearRect(0, 0, previewCanvasRef.current.width, previewCanvasRef.current.height);
    previewCtx.save();
    previewCtx.globalAlpha = 0.6;
    previewCtx.strokeStyle = color;
    previewCtx.lineWidth = lineWidth * 2;
    previewCtx.lineCap = "round";
    previewCtx.lineJoin = "round";
    previewCtx.setLineDash([5, 5]);
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    if (mode === "rect") {
      previewCtx.strokeRect(start.x, start.y, dx, dy);
    } else if (mode === "circle") {
      const r = Math.hypot(dx, dy);
      previewCtx.beginPath();
      previewCtx.arc(start.x, start.y, r, 0, Math.PI * 2);
      previewCtx.stroke();
    }
    previewCtx.restore();
  };

  const drawShapeFinal = (start, end) => {
    const ctx = getCtx();
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth * 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    if (mode === "rect") {
      ctx.strokeRect(start.x, start.y, dx, dy);
    } else if (mode === "circle") {
      const r = Math.hypot(dx, dy);
      ctx.beginPath();
      ctx.arc(start.x, start.y, r, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  };

  // Pan
  const handleMouseDown = (e) => {
    if (mode === "pan") {
      setIsPanning(true);
      startPan.current = {
        x: e.clientX,
        y: e.clientY,
        scrollLeft: boardRef.current.scrollLeft,
        scrollTop: boardRef.current.scrollTop,
      };
      return;
    }

    if (mode === "text") {
      placeText(e);
      return;
    }

    if (["rect", "circle"].includes(mode)) {
      shapeStart.current = getCoordinates(e);
      return;
    }

    startDrawing(e);
  };

  const handleMouseMove = (e) => {
    if (isPanning) {
      const dx = e.clientX - startPan.current.x;
      const dy = e.clientY - startPan.current.y;
      boardRef.current.scrollLeft = startPan.current.scrollLeft - dx;
      boardRef.current.scrollTop = startPan.current.scrollTop - dy;
      return;
    }

    if (shapeStart.current && (mode === "rect" || mode === "circle")) {
      const current = getCoordinates(e);
      drawShapePreview(shapeStart.current, current);
      lastPosRef.current = current;
      return;
    }

    draw(e);
  };

  const handleMouseUp = () => {
    if (isPanning) {
      setIsPanning(false);
      return;
    }

    if (shapeStart.current && (mode === "rect" || mode === "circle")) {
      drawShapeFinal(shapeStart.current, lastPosRef.current);
      const previewCtx = getPreviewCtx();
      if (previewCtx) {
        previewCtx.clearRect(0, 0, previewCanvasRef.current.width, previewCanvasRef.current.height);
      }
      shapeStart.current = null;
      saveState();
      return;
    }

    stopDrawing();
  };

  return (
    <div className="whiteboard-container">

      <div className="controls">
        <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />

        <input
          type="range"
          min="1"
          max="10"
          value={lineWidth}
          onChange={(e) => setLineWidth(e.target.value)}
        />

        <button className={`btn ${mode === "draw" ? "active" : ""}`} onClick={() => setMode("draw")}>✏️ Draw</button>
        <button className={`btn ${mode === "erase" ? "active" : ""}`} onClick={() => setMode("erase")}>🧽 Erase</button>
        <button className={`btn ${mode === "pan" ? "active" : ""}`} onClick={() => setMode("pan")}>🖐️ Pan</button>
        <button className={`btn ${mode === "text" ? "active" : ""}`} onClick={() => setMode("text")}>🅰️ Text</button>

        <button className={`btn ${mode === "rect" ? "active" : ""}`} onClick={() => setMode("rect")}>📦 Rect</button>
        <button className={`btn ${mode === "circle" ? "active" : ""}`} onClick={() => setMode("circle")}>⭕ Circle</button>
        <input
          className="text-input"
          type="text"
          value={textValue}
          onChange={(e) => setTextValue(e.target.value)}
          placeholder="Type label"
        />

        <button className="btn" onClick={undo}>↩️</button>
        <button className="btn" onClick={redo}>↪️</button>

        <button className="btn btn-clear" onClick={clearBoard}>Clear</button>
        <button className="btn btn-save" onClick={saveBoard}>Save</button>
      </div>

      <div
        className={`whiteboard-scroll ${mode}`}
        ref={boardRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div className="whiteboard-stage">
          <canvas ref={canvasRef} className="whiteboard-canvas" />
          <canvas ref={previewCanvasRef} className="whiteboard-preview" />
        </div>
      </div>
    </div>
  );
}
